import Resizer from "react-image-file-resizer";
import { api } from "@/utils/api";
import { getBackendCallErrorMessage } from "@/utils/backendErrorMessage";
import {
  createMetaConversionPayload,
  fireMetaPixelEvent,
} from "@/utils/metaPixel";
import type { RequestQuoteDraft } from "@/utils/requestQuoteDraft";
import { getRequestQuoteImageFiles } from "@/utils/requestQuoteDraft";

const escapeMarkdown = (text: string) =>
  text?.replace(/([_*[\]()~`>#+\-=|{}.!])/g, "\\$1") || "";

const wrapText = (text: string, maxLineLength = 80): string => {
  if (!text) return "";
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";
  for (const word of words) {
    if ((currentLine + word).length > maxLineLength) {
      lines.push(currentLine.trim());
      currentLine = "";
    }
    currentLine += word + " ";
  }
  if (currentLine.trim()) lines.push(currentLine.trim());
  return lines.join("\n");
};

const resizeImage = async (file: File) => {
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("Image must be under 10MB");
  }
  return new Promise<Blob>((resolve, reject) => {
    Resizer.imageFileResizer(
      file,
      1500,
      1500,
      "JPEG",
      90,
      0,
      (uri) => {
        if (uri instanceof Blob) resolve(uri);
        else reject(new Error("Failed to resize image"));
      },
      "blob"
    );
  });
};

export async function uploadRequestQuoteImages(files: File[]): Promise<string[]> {
  if (!files.length) return [];
  const uploadedUrls: string[] = [];
  for (const image of files) {
    const resizedImage = await resizeImage(image);
    const formData = new FormData();
    formData.append("file", resizedImage);
    const response = await api.updateJobPhoto(formData);
    if (response && response.success) {
      uploadedUrls.push(response.data || response);
    } else {
      throw new Error(
        response?.message || "Failed to upload image"
      );
    }
  }
  return uploadedUrls;
}

export function formatRequestQuoteChatMessage(params: {
  draft: RequestQuoteDraft;
  jobUid?: string;
}) {
  const { draft, jobUid } = params;
  const { form } = draft;
  const safeDescription = wrapText(escapeMarkdown(form.description));
  const safeBudget = wrapText(escapeMarkdown(form.budgetDetails));
  const safeAddress = wrapText(escapeMarkdown(form.location?.address || ""));
  const startLabel = form.startDateISO
    ? new Date(form.startDateISO).toLocaleString()
    : "";

  return `📋 **New Quote Request**

${form.categoryName ? `Service Category: ${form.categoryName}` : ""}
${form.serviceName ? `Service Needed: ${form.serviceName}` : ""}
${safeDescription ? `Project Description:\n${safeDescription}` : ""}
${safeAddress ? `Location:\n${safeAddress}` : ""}
${startLabel ? `Preferred Start Date: ${startLabel}` : ""}
${form.urgency ? `Urgency: ${form.urgency.charAt(0).toUpperCase() + form.urgency.slice(1)}` : ""}
${safeBudget ? `Budget Details:\n${safeBudget}` : ""}
Job ID: ${jobUid || ""}
Please provide your quote for this project.`.trim();
}

export function buildRequestQuoteJobPayload(params: {
  draft: RequestQuoteDraft;
  userId: string;
  customerStripeId?: string;
  uploadedImageUrls: string[];
}) {
  const { draft, userId, customerStripeId, uploadedImageUrls } = params;
  const { form } = draft;
  const start = form.startDateISO ? new Date(form.startDateISO) : new Date();

  const jobData: Record<string, any> = {
    description: form.description,
    service: form.selectedService,
    category: form.selectedCategoryId,
    urgency: form.urgency,
    isDirectHire: false,
    isProposalSent: false,
    user: userId,
    imageUrls: uploadedImageUrls,
    customerStripeId: customerStripeId || "dscse",
    schedule: {
      startDateTime: start,
      endDateTime: start,
    },
    budget: 0,
  };

  if (form.location) {
    jobData.location = {
      type: "Point",
      coordinates: [form.location.lng, form.location.lat],
      value: form.location.address,
    };
  }

  return jobData;
}

export type SubmitRequestQuoteResult = {
  success: boolean;
  message?: string;
  sent?: number;
  job?: any;
};

export async function submitRequestQuoteFromDraft(params: {
  draft: RequestQuoteDraft;
  userId: string;
  customerStripeId?: string;
}): Promise<SubmitRequestQuoteResult> {
  const { draft, userId, customerStripeId } = params;
  const { form, mode, selectedProviders } = draft;

  if (!form.description?.trim()) {
    return { success: false, message: "Please provide a project description." };
  }
  if (!form.selectedCategoryId || !form.selectedService) {
    return { success: false, message: "Please select a service category and service." };
  }
  if (!form.location) {
    return { success: false, message: "Please select a service location." };
  }
  if (!form.startDateISO) {
    return { success: false, message: "Please select a start date and time." };
  }
  if (mode === "manual" && selectedProviders.length === 0) {
    return { success: false, message: "Please select at least one provider." };
  }
  if (selectedProviders.some((p) => p.userId === userId)) {
    return { success: false, message: "You cannot send a quote to yourself." };
  }

  const files = getRequestQuoteImageFiles();
  let uploadedImageUrls: string[] = [];
  try {
    uploadedImageUrls = await uploadRequestQuoteImages(files);
  } catch (e: any) {
    return { success: false, message: e?.message || "Failed to upload images" };
  }

  const jobData = buildRequestQuoteJobPayload({
    draft,
    userId,
    customerStripeId,
    uploadedImageUrls,
  });

  const jobRes = await api.createRequestQuoteJob(jobData);
  if (!jobRes?.success) {
    return {
      success: false,
      message:
        getBackendCallErrorMessage(jobRes) ||
        jobRes?.message ||
        "Failed to create quote job",
    };
  }

  const job = jobRes.data?.data || jobRes.data;
  const requestQuoteJobMongoId = job?._id;
  if (!requestQuoteJobMongoId) {
    return { success: false, message: "Failed to create quote job" };
  }

  const recipientProviderIds =
    mode === "manual"
      ? selectedProviders
          .map((p) => String(p.userId))
          .filter((id) => id && id !== "undefined" && id !== String(userId))
      : [];

  if (mode === "manual" && recipientProviderIds.length === 0) {
    return {
      success: false,
      message: "Please select at least one valid provider.",
    };
  }

  const messageText = formatRequestQuoteChatMessage({
    draft,
    jobUid: job.uid,
  });

  const meta = createMetaConversionPayload();
  const categoryName = form.categoryName || "Other";

  const response = await api.sendRequestQuote({
    jobId: requestQuoteJobMongoId,
    message: messageText,
    imageUrls: uploadedImageUrls.length ? uploadedImageUrls : undefined,
    providerIds: recipientProviderIds.length ? recipientProviderIds : undefined,
    ...(recipientProviderIds.length === 0 ? { maxDistance: 50 } : {}),
    ...meta,
  });

  if (!response?.success) {
    return {
      success: false,
      message:
        getBackendCallErrorMessage(response) ||
        response?.message ||
        "Failed to send request quote",
    };
  }

  fireMetaPixelEvent("Lead", meta.eventId, { content_category: categoryName });

  const sent =
    typeof response?.data?.sent === "number" ? response.data.sent : 0;

  return { success: true, sent, job };
}
